<template>
  <div
    class="topNav"
    v-show="authStore.authVariablesSet"
    data-test="top-nav"
  >
    <div class="topContent">
      <div
        class="host-image"
        data-test="host-image"
      >
        <img
          v-if="donorStore.hasHostLogo"
          :src="donorStore.hostLogo"
          alt="Hero Image"
        />
      </div>

      <div class="flex align-center"></div>
      <div class="buttonsRight">
        <Button
          data-test="sidebar-toggle"
          class="pi pi-bars sidebar-toggle p-button-secondary"
          @click="toggleMenu"
          aria-label="Toggle Menu"
        />
      </div>
    </div>
  </div>
  <div
    class="main-container flex flex-grow"
    v-show="authStore.authVariablesSet"
    data-test="main-container"
  >
    <Transition :name="isMobile ? 'sidebar-slide' : null">
      <div
        class="sidebar shadow-md lg:shadow-none flex flex-col flex-grow fixed left-0 w-[300px] bg-white"
        v-show="!isMobile || (openMenu && showSidebar)"
        data-test="sidebar"
      >
        <ul
          id="nav"
          class="flex flex-col flex-grow"
          data-test="nav"
        >
          <li
            style="margin: 15px"
            data-test="donor-info"
          >
            <h2>
              {{ donorStore.donorInfo.firstName }}
              {{ donorStore.donorInfo.lastName }}
            </h2>
            <p style="font-size: 14px">Donor Since {{ formatMonthYear(donorStore.donorInfo.enteredDate) }}</p>
            <p
              v-if="companyAdminStore.existingActiveProfile || companyAdminStore.existingPendingProfile"
              style="font-size: 14px"
              class="underline cursor-pointer underline-offset-4 font-medium"
              @click="authStore.showAuthSwitchDialog = true"
              data-test="switch-account"
            >
              Switch to {{ authStore.type == 'companyAccount' ? 'Donor' : 'Company' }} Account
            </p>
            <AuthSwitchAccount />
          </li>
          <li style="width: 90%"><hr /></li>
          <li
            v-for="item in navItems"
            :key="item.name"
            :class="{ 'bg-primary-300': item.isActive }"
            @click="
              item.callback ? item.callback() : router.push(item.route);
              toggleMenu();
            "
            class="router-link hover:bg-primary-300"
            :data-test="'nav-item-' + item.label.replace(/\s+/g, '-')"
          >
            <div
              class="menu-item"
              v-if="typeof item.if == 'undefined' || item.if"
            >
              <img
                class="icon"
                :src="item.icon"
                :alt="item.alt"
              />
              <div>{{ item.label }}</div>
            </div>
          </li>
        </ul>
        <div class="p-4 pb-12">
          <Button
            class="w-full text-lg"
            custom-font="false"
            :style="`color: ${getContrastYIQ(donorStore.mainColor)}`"
            @click="logOutUser"
            data-test="logout-btn"
            label="Log Out"
          />
        </div>
      </div>
    </Transition>

    <div
      class="main-content"
      ref="contentContainer"
      data-test="main-content"
    >
      <router-view />
      <PortalFooter />
    </div>
    <!-- Snackbar component -->
    <Snackbar />
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onBeforeMount, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import CompanyOverviewIcon from '@/assets/CompanyOverviewIcon.svg';
import CompanyPublicPageIcon from '@/assets/CompanyPublicPageIcon.svg';
import OverviewIcon from '@/assets/OverviewIcon.svg';
import FundraisersIcon from '@/assets/FundraisersIcon.svg';
import FavoriteCausesIcon from '@/assets/FavoriteCausesIcon.svg';
import SettingsIcon from '@/assets/SettingsIcon.svg';
import SingleHandHoldingHeart from '@/assets/SingleHandHoldingHeart.svg';
import HomeIcon from '@/assets/HomeIcon.svg';
import MenuHamburger from '@/assets/MenuHamburger.svg';
import { companyGivingStatus as companyGivingStatusEnum } from '@/helpers/enum.js';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useAuthStore } from '@/stores/AuthStore.js';
import { usePaginationStore } from '@/stores/PaginationStore.js';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore.js';
import getContrastYIQ from '@/helpers/getContrastYIQ';
import Snackbar from '@/components/Snackbar.vue';
import PortalFooter from '@/components/PortalFooter.vue';
import useIsMobile from '@/composables/useIsMobile.js';
import AuthSwitchAccount from '@/components/auth/AuthSwitchAccount.vue';

const { isMobile } = useIsMobile(1024);

const authStore = useAuthStore();
const donorStore = useDonorStore();
const paginationStore = usePaginationStore();
const companyAdminStore = useCompanyAdminStore();
const router = useRouter();
const route = useRoute();

const openMenu = ref(false);
const showSidebar = ref(false);

const toggleMenu = () => {
  openMenu.value = !openMenu.value;

  // Scroll to top of page on nav
  document.querySelector('.main-container').scrollTo(0, 0);
};
window.addEventListener('resize', () => {
  if (window.innerWidth < 1024) {
    showSidebar.value = true;
  } else {
    showSidebar.value = false;
  }
});

const contentContainer = ref(null);

const ro = new ResizeObserver(() => {
  setDonationsToDisplay();
});

const setDonationsToDisplay = () => {
  const cardContentHeight = contentContainer?.value?.clientHeight;

  const header = 520;
  if (cardContentHeight <= 960 || isMobile.value) {
    //if 1080px without header or mobile
    paginationStore.itemsPerPage = 10;
    return;
  }
  const rowHeight = 106;

  const rows = Math.floor((cardContentHeight - header) / rowHeight);

  paginationStore.itemsPerPage = rows ? rows : 1;
};

onBeforeMount(async () => {
  if (authStore.type == 'companyAccount') {
    // If the user is logged in via a company account, make sure the company is loaded
    if (!companyAdminStore.company) {
      authStore.authVariablesSet = false;
      await companyAdminStore.getCompany();
      authStore.authVariablesSet = true;
    }

    // If the company isn't approved yet, they can't do anything. Prompt them to switch back to donor account.
    if (companyAdminStore.company && companyAdminStore.company.status == companyGivingStatusEnum.PENDING_APPROVAL) {
      authStore.showAuthSwitchDialog = true;
      authStore.authVariablesSet = false;

      // If the modal is closed and the user is still here as a company user, reload the page to show it again.
      watch(
        () => authStore.showAuthSwitchDialog,
        (value) => {
          if (!value) {
            setTimeout(() => {
              if (authStore.type == 'companyAccount') {
                window.location.reload();
              }
            }, 4000);
          }
        },
      );
    }
  }
});

onMounted(() => {
  ro.observe(contentContainer.value);
  showSidebar.value = window.innerWidth < 1024;
});

// Define navigation items
const navItems = computed(() => [
  //Donor Account
  {
    name: 'Overview',
    label: 'Overview',
    icon: OverviewIcon,
    alt: 'forms-builder',
    route: '/',
    if: route.meta.accountType == 'donorAccount',
    isActive: router.currentRoute.value.name === 'Overview',
  },
  {
    name: 'DonationHistory',
    label: 'Donation History',
    icon: SingleHandHoldingHeart,
    alt: 'theme-builder',
    route: '/donationHistory',
    if: route.meta.accountType == 'donorAccount',
    isActive: router.currentRoute.value.name === 'DonationHistory',
  },
  {
    name: 'Fundraisers',
    label: 'Fundraisers',
    icon: FundraisersIcon,
    alt: 'settings',
    route: '/fundraisers',
    if: donorStore.featureCampaigns,
    if: route.meta.accountType == 'donorAccount',
    isActive: router.currentRoute.value.name === 'Fundraisers',
  },
  {
    name: 'FavoriteCauses',
    label: 'Favorite Causes',
    icon: FavoriteCausesIcon,
    alt: 'settings',
    route: '/favoriteCauses',
    if: route.meta.accountType == 'donorAccount',
    isActive: router.currentRoute.value.name === 'FavoriteCauses',
  },
  {
    name: 'Settings',
    label: 'Settings',
    icon: SettingsIcon,
    alt: 'settings',
    route: '/settings',
    if: route.meta.accountType == 'donorAccount',
    isActive: router.currentRoute.value.name === 'Settings',
  },
  {
    name: 'Overview',
    label: 'Overview',
    icon: CompanyOverviewIcon,
    alt: 'overview',
    route: '/company/overview',
    if: route.meta.accountType == 'companyAccount',
    isActive: router.currentRoute.value.name === 'CompanyOverview',
  },
  {
    name: 'EmployeeGiving',
    label: 'Employee Giving',
    icon: OverviewIcon,
    alt: 'employee-giving',
    route: '/company/employee-giving',
    if: route.meta.accountType == 'companyAccount',
    isActive: router.currentRoute.value.name === 'EmployeeGiving',
  },
  {
    name: 'PublicPage',
    label: 'My Public Page',
    icon: CompanyPublicPageIcon,
    alt: 'public-page',
    route: '/company/public-page',
    if: route.meta.accountType == 'companyAccount',
    isActive: router.currentRoute.value.name === 'CompanyPublicPage',
  },
  {
    name: 'Fundraisers',
    label: 'Fundraisers',
    icon: FundraisersIcon,
    alt: 'fundraisers',
    route: '/company/fundraisers',
    if: route.meta.accountType == 'companyAccount',
    isActive: router.currentRoute.value.name === 'CompanyFundraisers',
  },
  {
    name: 'FeaturedCauses',
    label: 'Featured Causes',
    icon: FavoriteCausesIcon,
    alt: 'featured-causes',
    route: '/company/featured-causes',
    if: route.meta.accountType == 'companyAccount',
    isActive: router.currentRoute.value.name === 'CompanyFeaturedCauses',
  },
  {
    name: 'SettingsAndUsers',
    label: 'Settings and Users',
    icon: SettingsIcon,
    alt: 'settings-and-users',
    route: '/company/settings',
    if: route.meta.accountType == 'companyAccount',
    isActive: router.currentRoute.value.name === 'CompanySettingsAndUsers',
  },

  {
    name: 'ReturnHome',
    label: 'Return to Gives Day Home',
    icon: HomeIcon,
    alt: 'ReturnHome',
    callback: () => {
      window.location.href = donorStore.domain;
    },
  },
]);

const formatMonthYear = (dateString) => {
  const date = new Date(dateString);
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();
  return `${month} ${year}`;
};

const logOutUser = () => {
  authStore.logout();
  // router.push("/login");
};
</script>

<style scoped>
.logout-btn {
  margin: 0 16px 48px 16px;
}
.sidebar {
  transition: transform 0.3s ease;
  z-index: 1000;
  top: 72px;
  bottom: 0;
}

.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition: transform 0.5s ease;
}

.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
  transform: translateX(-100%);
}

.sidebar-toggle {
  display: none; /* Hide the button by default */
}
#nav {
  display: flex;
  flex-direction: column;
  position: relative;
  background: #ffffff;
  z-index: 6000;

  hr {
    border-top: solid 2px var(--p-primary-500);
    margin: 0.5rem 0 1rem;
    opacity: 0.25;
  }
}
.host-image {
  padding: 4px 4px 4px 15px;
  height: 72px;
  display: flex;
  justify-content: center;
}
.host-image img {
  height: 100%;
  object-fit: contain;
}
.icon {
  margin: 15px;
  width: 25px;
}
.selected {
  background-color: #e2f2fe;
}
a {
  display: flex;
  align-items: center;
}

.topNav {
  position: fixed;
  top: 0;
  height: 72px;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background: #ffffff;
  z-index: 1001;
}
.back {
  margin: 25px;
}
.topContent {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-content: center;
  align-items: center;
  justify-content: space-between;
  width: 100vw;
}

.buttonsRight {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-content: center;
  align-items: center;
  justify-content: flex-end;
}

.title {
  font-weight: 500;
  color: #002d41;
  margin-left: 30px;
}
.menu-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 60px;
  cursor: pointer;
}
.router-link {
  text-decoration: none; /* Remove underline */
  color: inherit;
  width: 100%;
  transition: background-color 0.3s ease;
}
.main-container {
  display: flex;
  flex-direction: row;
  margin-top: 60px;
  overflow-y: auto;
  overflow-x: none;
}
.main-content {
  flex: 1;
  margin-left: 300px;
}

@media screen and (max-width: 1024px) {
  .sidebar {
    width: 100vw;
  }

  #nav {
    width: 100vw;
  }

  .main-content {
    margin-left: 0;
  }

  .sidebar-toggle {
    display: block;
    width: 50px;
    margin-right: 5px;
  }
}
</style>
